import { Controller } from '@hotwired/stimulus';

/**
 * OTP Input Controller
 *
 * Handles 6-digit OTP code input with the following features:
 * - Auto-focus on first input when connected
 * - Auto-advance to next input after typing
 * - Backspace to previous input when empty
 * - Paste support for full code
 * - Number-only validation
 * - Auto-submit when all 6 digits entered (optional)
 */
export default class extends Controller {
  static targets = ['input'];
  static values = {
    autoSubmit: { type: Boolean, default: false }
  };

  connect() {
    console.log('OTP input controller connected');

    // Focus on first input when controller connects
    if (this.hasInputTarget && this.inputTargets.length > 0) {
      this.inputTargets[0].focus();
    }

    // Add event listeners to all inputs
    this.setupEventListeners();
  }

  disconnect() {
    // Clean up event listeners if needed
    this.inputTargets.forEach(input => {
      input.removeEventListener('input', this.handleInputBound);
      input.removeEventListener('keydown', this.handleKeydownBound);
      input.removeEventListener('paste', this.handlePasteBound);
      input.removeEventListener('focus', this.handleFocusBound);
    });
  }

  setupEventListeners() {
    // Bind event handlers to maintain 'this' context
    this.handleInputBound = this.handleInput.bind(this);
    this.handleKeydownBound = this.handleKeydown.bind(this);
    this.handlePasteBound = this.handlePaste.bind(this);
    this.handleFocusBound = this.handleFocus.bind(this);

    this.inputTargets.forEach(input => {
      // Handle input - auto-advance to next field
      input.addEventListener('input', this.handleInputBound);

      // Handle keydown - backspace to previous field
      input.addEventListener('keydown', this.handleKeydownBound);

      // Handle paste - distribute pasted code across inputs
      input.addEventListener('paste', this.handlePasteBound);

      // Handle focus - select all text for easy replacement
      input.addEventListener('focus', this.handleFocusBound);
    });
  }

  handleInput(event) {
    const input = event.target;
    let value = input.value;

    // Only allow numeric characters
    value = value.replace(/\D/g, '');

    // Limit to 1 character
    if (value.length > 1) {
      value = value.charAt(0);
    }

    input.value = value;

    // Auto-advance to next input if a digit was entered
    if (value.length === 1) {
      this.focusNextInput(input);
    }

    // Check if all inputs are filled
    this.checkComplete();
  }

  handleKeydown(event) {
    const input = event.target;

    // Handle backspace on empty input - move to previous
    if (event.key === 'Backspace' && input.value === '') {
      event.preventDefault();
      this.focusPreviousInput(input);
    }

    // Handle left arrow key
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusPreviousInput(input);
    }

    // Handle right arrow key
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusNextInput(input);
    }

    // Only allow numeric keys, backspace, delete, tab, and arrow keys
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight',
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
    ];

    if (!allowedKeys.includes(event.key) && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
    }
  }

  handlePaste(event) {
    event.preventDefault();

    // Get pasted data
    const paste = (event.clipboardData || window.clipboardData).getData('text');

    // Extract only digits
    const digits = paste.replace(/\D/g, '');

    if (digits.length === 0) return;

    // Distribute digits across inputs
    const currentIndex = this.inputTargets.indexOf(event.target);
    const availableInputs = this.inputTargets.slice(currentIndex);

    for (let i = 0; i < Math.min(digits.length, availableInputs.length); i++) {
      availableInputs[i].value = digits[i];
    }

    // Focus on the last filled input or the input after if not all filled
    const lastFilledIndex = currentIndex + Math.min(digits.length, availableInputs.length) - 1;
    if (lastFilledIndex < this.inputTargets.length - 1) {
      this.inputTargets[lastFilledIndex + 1].focus();
    } else {
      this.inputTargets[lastFilledIndex].focus();
    }

    // Check if all inputs are filled
    this.checkComplete();
  }

  handleFocus(event) {
    // Select all text in the input for easy replacement
    event.target.select();
  }

  focusNextInput(currentInput) {
    const currentIndex = this.inputTargets.indexOf(currentInput);
    if (currentIndex < this.inputTargets.length - 1) {
      this.inputTargets[currentIndex + 1].focus();
    }
  }

  focusPreviousInput(currentInput) {
    const currentIndex = this.inputTargets.indexOf(currentInput);
    if (currentIndex > 0) {
      const previousInput = this.inputTargets[currentIndex - 1];
      previousInput.focus();
      // Clear the previous input's value when backspacing into it
      previousInput.value = '';
    }
  }

  checkComplete() {
    // Check if all inputs are filled
    const allFilled = this.inputTargets.every(input => input.value.length === 1);

    if (allFilled && this.autoSubmitValue) {
      // Auto-submit the form if all digits are entered
      const form = this.element.closest('form');
      if (form) {
        // Small delay to ensure last input is processed
        setTimeout(() => {
          form.requestSubmit();
        }, 100);
      }
    }
  }

  // Public method to get the complete OTP code
  getCode() {
    return this.inputTargets.map(input => input.value).join('');
  }

  // Public method to clear all inputs
  clear() {
    this.inputTargets.forEach(input => {
      input.value = '';
    });
    if (this.inputTargets.length > 0) {
      this.inputTargets[0].focus();
    }
  }

  // Public method to check if the code is complete
  isComplete() {
    return this.inputTargets.every(input => input.value.length === 1);
  }
}
